import { UIMode } from 'library/ts/enums/UIMode';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';

/**
 * @copyright © Paramount Streaming
 * @description Backs `tag.API.TAG.uiMode()` / `getUiMode()` and Skin's completion hook
 * `tag.notifyUiModeChangeDone(mode)`. Owns the in-flight lock and the current-mode state.
 *
 * Lifecycle of one accepted call:
 *  1. `requestMode(mode)` validates, applies idempotency + re-entrancy checks, sets the
 *     in-flight lock, updates `#currentMode`, and dispatches TagEvent.ON_UI_MODE_CHANGE_REQUEST.
 *  2. If no Skin is attached, `notifyDone(mode)` is invoked synchronously so the
 *     REQUEST → DONE contract holds regardless of Skin presence.
 *  3. With a Skin attached, MiniPlayerManager swaps overlays and eventually calls
 *     `tag.notifyUiModeChangeDone(mode)`, which routes here to dispatch the
 *     ON_UI_MODE_CHANGE_DONE event and release the lock.
 *
 * Invalid modes log a warning and no-op (programmer error, but non-fatal). Idempotent
 * calls and re-entrant calls also no-op without dispatching.
 */
export class UIModeService extends AbstractTagClass {

	#currentMode: UIMode = UIMode.NORMAL;
	#inFlight: boolean = false;

	/**
	 * @description Persistent / "always-on" host flag — orthogonal to `#currentMode`. Declares that the
	 * Smart Tag instance is reused across assets with no page reload (e.g. the Pluto Convergence
	 * "always-on" web player, PLBCK-8496), so navigation-teardown paths (the end-card homepage redirect)
	 * must not perform a full-frame `top.location.href` that would destroy the host SPA. Deliberately NOT
	 * a `UIMode` value: UI mode is a single mutually-exclusive chrome slot (mini/normal/theatre), whereas
	 * "always-on" can coexist with any chrome. Latched via the optional 2nd arg of `requestMode(mode,
	 * alwaysOn)`: omitting the arg leaves it unchanged; passing an explicit boolean sets/clears it,
	 * independent of whether the mode transition itself is accepted.
	 */
	#alwaysOn: boolean = false;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'UIModeService'
			}
		);
	}

	/**
	 * @description Current *committed* UI mode — updated only when `notifyDone(mode)` fires,
	 * not at `requestMode()` time. Mid-transition reads return the previously committed mode,
	 * not the in-flight target. Defaults to `UIMode.NORMAL` until a successful mode swap completes.
	 */
	get currentMode(): UIMode {
		return this.#currentMode;
	}

	/**
	 * @description Whether a transition is currently in flight (REQUEST emitted, DONE pending).
	 * Exposed primarily for tests and diagnostics.
	 */
	get isInFlight(): boolean {
		return this.#inFlight;
	}

	/**
	 * @description Whether the host has declared this a persistent / "always-on" surface (see `#alwaysOn`).
	 * Backs `tag.API.TAG.getIsAlwaysOn()`. Read by the end-card homepage-redirect guard to skip a
	 * full-frame navigation that would tear down a reused-instance host.
	 */
	get isAlwaysOn(): boolean {
		return this.#alwaysOn;
	}

	/**
	 * @description Entry point backing `tag.API.TAG.uiMode(mode, alwaysOn?)`. Latches the optional
	 * persistent-host flag, then validates the mode, applies idempotency + re-entrancy rules, and
	 * dispatches TagEvent.ON_UI_MODE_CHANGE_REQUEST.
	 *
	 * - `alwaysOn` (optional): explicit boolean sets/clears `#alwaysOn`; omitted → left unchanged.
	 *   Applied first and independently of the mode transition, so `uiMode(currentMode, true)` still
	 *   latches the flag even though the (idempotent) mode change is a no-op, and an invalid mode still
	 *   honors a supplied flag before the warning short-circuit.
	 * - Invalid mode → log warning, no-op (mode only).
	 * - Mode already matches `#currentMode` → no-op, no REQUEST dispatched.
	 * - In-flight transition → log warning, no-op (no queueing, no abort).
	 *
	 * When no Skin is attached, the DONE event is synthesized synchronously here so the
	 * REQUEST → DONE contract holds regardless of Skin presence.
	 */
	requestMode = ( mode: unknown, alwaysOn?: boolean ): void => {
		// Orthogonal persistent-host flag — latched up front so it is honored regardless of whether the
		// mode transition below is accepted, idempotent, or invalid. Only an explicit boolean mutates it.
		if ( typeof alwaysOn === 'boolean' && alwaysOn !== this.#alwaysOn ) {
			this.#alwaysOn = alwaysOn;
			this?.logger?.log( `uiMode: alwaysOn → ${this.#alwaysOn}` );
		}

		if ( !this.#isValidMode( mode ) ) {
			this?.logger?.warn(
				`uiMode: invalid mode ${this.#describeMode( mode )}; expected '${UIMode.MINI}', '${UIMode.NORMAL}', or '${UIMode.THEATRE}'. Ignoring.`
			);
			return;
		}

		// `mode` is narrowed to UIMode by the #isValidMode type predicate above.
		if ( this.#inFlight ) {
			this?.logger?.warn( `uiMode: transition already in flight; ignoring '${mode}' request.` );
			return;
		}

		if ( mode === this.#currentMode ) {
			// Idempotent: target matches current state. No REQUEST/DONE pair dispatched.
			return;
		}

		this.#inFlight = true;
		// `#currentMode` is intentionally NOT updated here — it commits in `notifyDone()` so
		// `getUiMode()` reads the previously committed mode while a transition is in flight,
		// not the optimistic target. The in-flight lock above already blocks any second
		// `requestMode` call until DONE, so there's no race where the idempotency check below
		// could short-circuit a still-in-flight target.

		const tagEventData = this.getTagEventDataVO();
		tagEventData.uiMode = mode;
		this.dispatchTagEvent(
			this.getTagEventVO( TagEvent.ON_UI_MODE_CHANGE_REQUEST, this.tag, tagEventData, this, this.name )
		);

		// Skin-less synthesis: if no Skin is attached, complete the transition synchronously
		// so the REQUEST → DONE contract holds. dispatchTagEvent already attempted to deliver
		// to skin (Tag.dispatchTagEvent only forwards when a skin instance exists), so simply
		// finalizing here is safe.
		if ( !this.tag?.view?.skin ) {
			this.notifyDone( mode );
		}
	};

	/**
	 * @description Skin's completion hook, routed via `tag.notifyUiModeChangeDone(mode)`.
	 * Dispatches TagEvent.ON_UI_MODE_CHANGE_DONE and releases the in-flight lock.
	 *
	 * Calls received outside an in-flight transition log a warning and no-op — this guards
	 * against a Skin that fires DONE twice or fires DONE without a matching REQUEST.
	 */
	notifyDone = ( mode: UIMode ): void => {
		if ( !this.#inFlight ) {
			this?.logger?.warn( `notifyUiModeChangeDone: no transition in flight; ignoring '${mode}'.` );
			return;
		}

		// Commit the new mode at the DONE boundary so `getUiMode()` reflects the swap only
		// once the Skin overlay (or skin-less synthesis) has signaled completion.
		this.#currentMode = mode;
		this.#inFlight = false;

		const tagEventData = this.getTagEventDataVO();
		tagEventData.uiMode = mode;
		this.dispatchTagEvent(
			this.getTagEventVO( TagEvent.ON_UI_MODE_CHANGE_DONE, this.tag, tagEventData, this, this.name )
		);
	};

	#isValidMode = ( mode: unknown ): mode is UIMode => {
		return mode === UIMode.MINI || mode === UIMode.NORMAL || mode === UIMode.THEATRE;
	};

	#describeMode = ( mode: unknown ): string => {
		if ( typeof mode === 'string' ) {
			return `'${mode}'`;
		}
		if ( mode === null ) {
			return 'null';
		}
		if ( mode === undefined ) {
			return 'undefined';
		}
		return typeof mode;
	};

}